import type { components } from "../../../api/generated-schema";
import { apiRequest } from "../../../api/httpClient";
import { getCemetery, type CemeteryData } from "../cemeteryApi";
import type { GisIdentityStatus, GisPositionState } from "./gisVocabulary";

export type CemeteryMapData = components["schemas"]["CemeteryMapData"];
export type CemeteryMapLayer = components["schemas"]["CemeteryMapLayer"];
export type CemeteryMapFallbackItem = components["schemas"]["CemeteryMapFallbackItem"];
export type GeoJsonFeature = components["schemas"]["GeoJsonFeature"];
export type GeoJsonFeatureCollection = components["schemas"]["GeoJsonFeatureCollection"];
export type GeoJsonGeometry = components["schemas"]["GeoJsonGeometry"];
export type GeoJsonPoint = components["schemas"]["GeoJsonPoint"];
export type GeoJsonPolygon = components["schemas"]["GeoJsonPolygon"];

export type GravePositionProperties = Readonly<{
  positionCode?: string;
  positionState?: GisPositionState;
  identityStatus?: GisIdentityStatus;
  /** Text TỰ DO dưới DB ("Nguyên trạng", "Cần tu bổ nắp"…) — không phải mã, không tra bảng. */
  physicalCondition?: string;
  sectionId?: string;
  sectionCode?: string;
  sectionName?: string;
  graveId?: string;
  graveCode?: string;
  /**
   * ⚠ KHÔNG có nguồn: từ vựng thuộc tính của feature bản đồ (`CemeteryMapQuery`) chỉ có
   * `positionCode`, `positionState`, `graveCode`, `graveId`, `sectionId`, `identityStatus`,
   * `physicalCondition`, `sectionType`, `sectionCode`, `sectionName` — KHÔNG khoá nào về liệt sĩ.
   * Trường này (và 7 trường cùng nhóm từng được thêm ở đây 18/09) vì vậy luôn `undefined`.
   * Thuộc tính liệt sĩ nằm ở `martyrLinks[]` của `GET /graves/{id}` — xem `GraveMartyrLinkSummary`.
   * Giữ lại chỉ vì `GisTableView.tsx:169` đang đọc nó; gỡ cần xử lý riêng cột "Liệt sĩ" của bảng.
   */
  martyrName?: string;
  precisionMeters?: number;
  sourceType?: string;
  verificationStatus?: "TEMPORARY" | "VERIFIED";
  lastVerifiedAt?: string;
  updatedAt?: string;
}>;

export type SectionProperties = Readonly<{
  sectionCode?: string;
  sectionName?: string;
  sectionType?: "AREA" | "BLOCK" | "ROW";
  parentSectionId?: string;
}>;

export type CemeteryMapQuery = Readonly<{
  bbox?: number[];
  layer?: "CEMETERY_BOUNDARY" | "SECTION" | "GRAVE_POSITION";
  sectionId?: string;
  positionState?: GisPositionState;
  identityStatus?: GisIdentityStatus;
  physicalCondition?: string;
}>;

function buildQueryString(query?: CemeteryMapQuery): string {
  if (!query) return "";
  const params = new URLSearchParams();
  if (query.bbox && query.bbox.length === 4) {
    params.set("bbox", query.bbox.join(","));
  }
  if (query.layer) params.set("layer", query.layer);
  if (query.sectionId) params.set("sectionId", query.sectionId);
  if (query.positionState) params.set("positionState", query.positionState);
  if (query.identityStatus) params.set("identityStatus", query.identityStatus);
  if (query.physicalCondition) params.set("physicalCondition", query.physicalCondition);
  const q = params.toString();
  return q ? `?${q}` : "";
}

export function getCemeteryMap(
  cemeteryId: string,
  query?: CemeteryMapQuery,
  signal?: AbortSignal,
): Promise<CemeteryMapData> {
  const qs = buildQueryString(query);
  return apiRequest<CemeteryMapData>(
    `/cemeteries/${encodeURIComponent(cemeteryId)}/map${qs}`,
    signal ? { signal } : {},
  );
}

export type GravePositionGeometryData = Readonly<{
  positionId: string;
  footprint?: GeoJsonPolygon | null;
  location?: GeoJsonPoint | null;
  version: number;
}>;

export type SetGravePositionGeometryPayload = Readonly<{
  footprint?: GeoJsonPolygon | null;
  location?: GeoJsonPoint | null;
  expectedVersion: number;
}>;

/**
 * Kiểu import lấy THẲNG từ hợp đồng đã sinh — không khai tay.
 *
 * Bản trước khai tay một model hư cấu (`batchId`/`errorRows`/`rowNumber`/`identifierCode`/
 * `actionType`/`{items,total,…}`) không khớp một trường nào của `ncc-api.yaml` lẫn DTO backend,
 * nên đọc `rowPage.items` ra `undefined` ⇒ bảng rỗng, và `actionType` thì không tồn tại ở đâu cả.
 */
export type GisImportBatchData = components["schemas"]["GisImportBatchData"];
export type GisImportRowData = components["schemas"]["GisImportRowData"];
export type GisImportRowPage = components["schemas"]["GisImportRowPage"];
/** `UPLOADED · VALIDATING · VALIDATED · COMMITTING · COMMITTED · PARTIAL_COMMIT · FAILED`. */
export type GisImportBatchStatus = GisImportBatchData["status"];
/** `PENDING · VALID · QUARANTINED · COMMITTED · FAILED`. */
export type GisImportRowStatus = GisImportRowData["status"];
export type GisTargetLayer = GisImportBatchData["targetLayer"];

export type PublicGraveProfile = Readonly<{
  kind: "martyr" | "unidentified" | "collective" | "multiple" | "restricted";
  martyrName?: string;
  birthYear?: number | null;
  deathYear?: number | null;
  hometown?: string | null;
  cemeteryName?: string;
  graveCode?: string;
  positionPath?: string;
  burialState?: string;
}>;

export function setGravePositionGeometry(
  positionId: string,
  payload: SetGravePositionGeometryPayload,
  idempotencyKey?: string,
): Promise<GravePositionGeometryData> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (idempotencyKey) {
    headers["Idempotency-Key"] = idempotencyKey;
  } else {
    headers["Idempotency-Key"] = `geom-${positionId}-${Date.now()}`;
  }

  return apiRequest<GravePositionGeometryData>(
    `/grave-positions/${encodeURIComponent(positionId)}/geometry`,
    {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Bốn operation nhập GIS — đường dẫn theo `GisImportController.java:78,107,115,135`.
 *
 * Cả bốn đều nằm DƯỚI `/cemeteries/{cemeteryId}`; thiếu tiền tố là 404 (Spec S12.5.3 §3).
 */
export async function createGisImport(
  cemeteryId: string,
  file: File,
  metadata: { targetLayer: GisTargetLayer; sourceSrid: number },
): Promise<GisImportBatchData> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append(
    "metadata",
    new Blob([JSON.stringify(metadata)], { type: "application/json" }),
  );

  return apiRequest<GisImportBatchData>(
    `/cemeteries/${encodeURIComponent(cemeteryId)}/gis-imports`,
    {
      method: "POST",
      body: formData,
    },
  );
}

export function getGisImportBatch(
  cemeteryId: string,
  batchId: string,
): Promise<GisImportBatchData> {
  return apiRequest<GisImportBatchData>(
    `/cemeteries/${encodeURIComponent(cemeteryId)}/gis-imports/${encodeURIComponent(batchId)}`,
  );
}

export function getGisImportRows(
  cemeteryId: string,
  batchId: string,
  query?: { status?: GisImportRowStatus; page?: number; size?: number },
): Promise<GisImportRowPage> {
  const params = new URLSearchParams();
  if (query?.status) params.set("status", query.status);
  // `!= null` chứ không phải truthy: `page=0` là trang ĐẦU theo backend 0-based
  // (`GisImportController.java:119`) — coi 0 là falsy sẽ lặng lẽ bỏ mất tham số.
  if (query?.page != null) params.set("page", String(query.page));
  if (query?.size != null) params.set("size", String(query.size));
  const qs = params.toString() ? `?${params.toString()}` : "";

  return apiRequest<GisImportRowPage>(
    `/cemeteries/${encodeURIComponent(cemeteryId)}/gis-imports/${encodeURIComponent(batchId)}/rows${qs}`,
  );
}

export function commitGisImport(
  cemeteryId: string,
  batchId: string,
  idempotencyKey?: string,
): Promise<GisImportBatchData> {
  const headers: Record<string, string> = {
    // Khoá phải ỔN ĐỊNH theo batch: nhồi `Date.now()` vào đây làm mỗi lần retry sinh một khoá
    // mới, mất hẳn tính idempotent mà backend dựa vào để xử lý `PARTIAL_COMMIT`.
    "Idempotency-Key": idempotencyKey ?? `commit-${batchId}`,
  };

  return apiRequest<GisImportBatchData>(
    `/cemeteries/${encodeURIComponent(cemeteryId)}/gis-imports/${encodeURIComponent(batchId)}/commit`,
    {
      method: "POST",
      headers,
    },
  );
}

export function getPublicGraveProfile(
  cemeterySlug: string,
  graveCode: string,
): Promise<PublicGraveProfile> {
  return apiRequest<PublicGraveProfile>(
    `/public/cemeteries/${encodeURIComponent(cemeterySlug)}/graves/${encodeURIComponent(graveCode)}`,
  );
}

export { getCemetery, type CemeteryData };

